/**
 * Sync liaison coparenting serveur ↔ local — Phase 4, incrément 4.10.2.
 *
 * Deuxième sub-entité **coparenting** synchronisée. Étend ADR 0009
 * (server-wins) au champ `data.liaison` des foyers coparenting.
 *
 * Différence avec journal-sync (4.10.1) :
 *   - Les transmissions ont une machine d'état mutable (status,
 *     signatureRequested). La sync 4.10.2 ne pousse PAS les
 *     mutations locales — elle se contente de remonter les entrées
 *     pré-auth (non-UUID) via POST et d'écraser le local avec le
 *     fetch GET (server-wins). Les mutations status passent par
 *     `/sign` ou PATCH au moment où l'UI les déclenche (à câbler
 *     plus tard dans 4.10.2+ ou Phase 5 temps-réel). Pour l'instant
 *     la machine d'état reste locale tant qu'aucun appareil tiers
 *     ne mute ; ce sub-sync rapatrie ces mutations à chaque login.
 *
 * Idempotent, filtre `home.mode === 'coparenting'`, déclenché en
 * parallèle des autres sub-syncs par `homes-sync.ts`.
 */
import * as liaisonApi from '../api/liaison.js';
import { ApiError } from '../api/client.js';
import {
  applyLiaisonSyncResult,
  getHomesSnapshot,
  isServerHomeId,
  type ServerTransmissionPayload,
} from './homes.js';
import type { CoparentingHomeData, Transmission } from '@nestor/shared-types';

export interface LiaisonSyncStatus {
  state: 'idle' | 'syncing' | 'ok' | 'error';
  lastAt: number | null;
  errorCode: string | null;
}

let status: LiaisonSyncStatus = { state: 'idle', lastAt: null, errorCode: null };
let inFlight: Promise<void> | null = null;

export function getLiaisonSyncStatus(): LiaisonSyncStatus {
  return status;
}

/**
 * Synchronise la liaison pour tous les foyers UUID coparenting.
 * Idempotent.
 */
export async function syncLiaisonForAllHomes(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = doSync().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doSync(): Promise<void> {
  status = { state: 'syncing', lastAt: status.lastAt, errorCode: null };
  const { homes } = getHomesSnapshot();

  let anyError = false;
  let lastErrorCode: string | null = null;

  for (const home of homes) {
    if (!isServerHomeId(home.id)) continue;
    if (home.mode !== 'coparenting') continue;

    const data = home.data as CoparentingHomeData;

    // 1. Migration POST des transmissions locales pré-auth.
    // Phase 3.3.3 : offline-first via SendResult (cf. 3.3.2).
    const idMap = new Map<string, string>();
    const localOnly = data.liaison.filter((t) => !isServerHomeId(t.id));
    for (const t of localOnly) {
      try {
        const result = await liaisonApi.createTransmission(home.id, buildCreateInput(t), t.id);
        if (result.kind === 'sent') {
          idMap.set(t.id, result.data.entry.id);
        }
      } catch (err) {
        const code = errorCode(err);
        anyError = true;
        lastErrorCode = code;
        console.warn('[liaison-sync] migration POST échouée', {
          homeId: home.id,
          transmissionId: t.id,
          error: code,
        });
      }
    }

    // 2. Fetch + 3. apply.
    try {
      const serverEntries = await liaisonApi.listTransmissions(home.id);
      const payload: ServerTransmissionPayload[] = serverEntries.map((e) => ({
        id: e.id,
        kind: e.kind,
        text: e.text,
        signatureRequested: e.signatureRequested,
        status: e.status,
        createdAt: e.createdAt,
      }));
      applyLiaisonSyncResult(home.id, { idMap, serverTransmissions: payload });
    } catch (err) {
      if (idMap.size > 0) {
        applyLiaisonSyncResult(home.id, { idMap });
      }
      const code = errorCode(err);
      anyError = true;
      lastErrorCode = code;
      console.warn('[liaison-sync] fetch /liaison échoué', { homeId: home.id, error: code });
    }
  }

  status = anyError
    ? { state: 'error', lastAt: Date.now(), errorCode: lastErrorCode }
    : { state: 'ok', lastAt: Date.now(), errorCode: null };
}

/**
 * Construit l'input POST à partir d'une transmission locale.
 *
 * **Note** : le status initial est dérivé côté serveur depuis
 * `kind` + `signatureRequested` (cf. `initialStatus` dans
 * `routes/liaison.ts`). On ne l'envoie pas — la valeur locale est
 * juste la valeur initiale, le serveur fait foi. Si le status local
 * avait déjà évolué (sign / accept) avant la migration, le merge
 * server-wins le ramènera à l'état initial — perte de données
 * mineure et attendue pour la phase 4.10.2 (pas de PATCH dans le
 * sub-sync). La mutation se rejouera côté UI au prochain user
 * interaction si nécessaire.
 */
function buildCreateInput(t: Transmission): liaisonApi.CreateTransmissionInput {
  return {
    kind: t.kind,
    text: t.text,
    signatureRequested: t.signatureRequested,
  };
}

function errorCode(err: unknown): string {
  if (err instanceof ApiError) {
    return err.code ?? `http_${err.status.toString()}`;
  }
  if (err instanceof Error) return err.name;
  return 'unknown';
}

/** @internal pour tests — reset le state module-level. */
export function _resetLiaisonSyncForTests(): void {
  status = { state: 'idle', lastAt: null, errorCode: null };
  inFlight = null;
}
